// Capability matrix — the ONE table answering "can THIS kind of object take
// THIS effect, and through which mechanism?" (universal-effects U1, owner
// 2026-07-21: "apply all the same effects to shapes and icons").
//
// Consulted by the engine's object-generic verbs (useCanvasEditor
// applyEffect/removeEffect/clearEffects) and by the U2 surface-ungating lane
// (tiles enable/disable + plain-words reasons). PURE DATA + pure functions,
// no imports — loadable anywhere (probes, workers) without dragging fabric
// or the editor in, same posture as fxRegistry.
//
// Modes:
//   'native'      — the effect rides plain fabric props (shadow slot; text's
//                   whole slot model; Hollow-on-shape's fill/stroke swap).
//                   Stock fabric consumers render it as-is: full portability.
//   'fx'          — the effect rides a data.fx filter step (the fxRender
//                   seam). Recipe portable (data is in JSON_KEYS), pixels
//                   live-only: see the DEGRADE notes per cell.
//   'unavailable' — denied, with the human reason U2 shows. The engine verbs
//                   no-op these honestly (returned in `denied`, never thrown).
//
// DEGRADE LAW (stock viewers — a consumer that loads the saved JSON with
// plain fabric and no fx seam), per effect:
//   shadow/lift/glow/echo — native object shadow on every kind: degrades to
//     ITSELF (full fidelity).
//   outline (text)        — native stroke props: full fidelity (the union-
//     contour band is a live-only visual upgrade; raw-path stroke degrade).
//   outline (icon)        — fx contour band only: stock viewers see the plain
//     icon, no band. Native image stroke does not exist, so there is no
//     portable approximation to emit.
//   hollow (text)         — native transparent fill + routed stroke: full
//     fidelity minus the union-contour upgrade.
//   hollow (shape)        — native transparent fill + native stroke carrying
//     the shape's parked fill paint: FULL fidelity in stock viewers.
//   hollow (icon)         — fx contour band + hidden body: stock viewers see
//     the plain icon (body back, no band). Deliberate: blanking the raster
//     natively would leave saved designs invisible in stock viewers.
//   trace (text)          — native dashed stroke + transparent fill: full
//     fidelity minus the union-contour upgrade.
//   trace (shape/icon)    — fx band + hidden body: stock viewers see the
//     plain object. Same never-blank-natively law as hollow-on-icon; also
//     the shape's native stroke belongs to the border tool (no-twins), so
//     trace must not write it.
//   sticker (text)        — native shadow + stroke-ring approximation (the
//     plate is live-only): stock viewers see ringed letters.
//   sticker (shape/icon)  — native drop shadow only; the plate is live-only.
//   bevel / extrude       — fx/source steps: stock viewers see the flat
//     object (the documented pre-existing class for text, unchanged).
//
// RECIPE KEY LAW (U1): text keeps writing data.textEffects — byte-identical
// writes, zero text regressions, every saved design and the 273-style
// manifest untouched. Non-text kinds write the generalized data.effects.
// ALL reads go through effectsBagOf (data.effects ?? data.textEffects), so
// designs saved before this lane load and work forever, and a future
// migration of text onto the general key needs no reader changes.

/** Slot-model effect ids (EFFECT_DEFS family — applied via the slot verbs). */
export const SLOT_EFFECT_IDS = ['shadow', 'lift', 'glow', 'echo', 'outline', 'hollow', 'trace', 'sticker'];

/** Depth effects: data.fx step ids the generic verbs also route (bevel is a
 * filter step, extrude a source step — no slot entry, no native half). */
export const DEPTH_EFFECT_IDS = ['bevel', 'extrude'];

/** The registered fx step id carrying non-text Outline/Hollow/Trace bands
 * (fxContourOutline's contourBand filter). Stable forever once persisted. */
export const CONTOUR_FX_STEP_ID = 'contourBand';

const AVAIL = { mode: 'native' };
const FX = { mode: 'fx' };
const NO = (reason) => ({ mode: 'unavailable', reason });

const PHOTO_NO = NO('Photo effects are not available yet');
const GROUP_NO = NO('Select a single object (effects apply per object)');

// kind × effect → capability. THE table (PLAN-universal-effects §matrix).
export const CAPABILITY_MATRIX = {
  text: {
    shadow: AVAIL, lift: AVAIL, glow: AVAIL, echo: AVAIL,
    outline: AVAIL, hollow: AVAIL, trace: AVAIL, sticker: AVAIL,
    bevel: FX, extrude: FX,
  },
  shape: {
    // Shadow-slot family: native object shadow (fabric shadows scale with
    // the object, so proportions survive a corner-drag resize for free).
    shadow: AVAIL, lift: AVAIL, glow: AVAIL, echo: AVAIL,
    // NOT an effect for shapes: the Row-2 border tool owns shape edges (the
    // no-twins law — two controls must never claim one stroke).
    outline: NO('Shape edges belong to the Border tool'),
    // Native: fill parks transparent, the parked fill recipe paints the
    // native stroke (the fill-hostage law generalized — data.hollowParked
    // remembers fill AND the border tool's prior stroke for restore).
    hollow: AVAIL,
    // fx contour band + hidden body — never the native stroke (border tool
    // owns it; see the degrade notes above).
    trace: { mode: 'fx', via: CONTOUR_FX_STEP_ID },
    // fx plate from the object silhouette + native drop shadow.
    sticker: { mode: 'fx', via: 'stickerShape' },
    bevel: FX,
    // U3 (2026-07-21): the extrude source accepts shape geometry — exact
    // vectors via silhouetteOf (fxExtrude header documents the whole map).
    extrude: FX,
  },
  icon: {
    shadow: AVAIL, lift: AVAIL, glow: AVAIL, echo: AVAIL,
    // Images have no native stroke band — all three are fx contour bands
    // around the raster's alpha silhouette (tint pipeline respected: the
    // silhouette is the tinted element's own alpha).
    outline: { mode: 'fx', via: CONTOUR_FX_STEP_ID },
    hollow: { mode: 'fx', via: CONTOUR_FX_STEP_ID },
    trace: { mode: 'fx', via: CONTOUR_FX_STEP_ID },
    sticker: { mode: 'fx', via: 'stickerShape' },
    bevel: FX,
    // U3 (2026-07-21): the icon's traced silhouette extrudes (sub-pixel
    // tracer → THREE Shapes; tint = the face color).
    extrude: FX,
  },
  photo: {
    shadow: PHOTO_NO, lift: PHOTO_NO, glow: PHOTO_NO, echo: PHOTO_NO,
    outline: PHOTO_NO, hollow: PHOTO_NO, trace: PHOTO_NO, sticker: PHOTO_NO,
    bevel: PHOTO_NO, extrude: PHOTO_NO,
  },
};

/**
 * Effect-capable kind of a live fabric object (or serialized JSON object —
 * only `type`/`isIcon`/`name` and the element src are read).
 *   'text'  — the Textbox/IText family (delegates to the text engine).
 *   'shape' — analytic fabric shapes (rect/circle/ellipse/triangle/polygon/
 *             polyline/path/line): geometry IS the silhouette.
 *   'icon'  — images flagged isIcon (the iconify tint pipeline) or whose
 *             source is an SVG (vector alpha): alpha IS the silhouette.
 *   'photo' — every other image (raster photos; also baked text3d/emboss
 *             images — those carry their own pipelines).
 *   null    — not effect-capable (groups/active selections per the
 *             per-object law, the clip, unknown types).
 */
export function objectKindOf(obj) {
  if (!obj || obj.name === 'clip') return null;
  const t = String(obj.type || '').toLowerCase();
  if (t === 'text' || t === 'i-text' || t === 'itext' || t === 'textbox') return 'text';
  if (t === 'image') {
    if (obj.isIcon === true) return 'icon';
    const el = obj._originalElement || obj._element;
    const src = (el && (el.currentSrc || el.src)) || obj.src || '';
    if (/^data:image\/svg|\.svg([?#]|$)/i.test(src)) return 'icon';
    return 'photo';
  }
  if (t === 'rect' || t === 'circle' || t === 'ellipse' || t === 'triangle'
    || t === 'polygon' || t === 'polyline' || t === 'path' || t === 'line') return 'shape';
  return null;
}

/** Capability cell for kind × effect. Unknown kind (groups, null) and unknown
 * ids come back 'unavailable' with a reason — the verbs and the UI both lean
 * on this never returning undefined. */
export function effectCapability(kind, effectId) {
  const row = kind ? CAPABILITY_MATRIX[kind] : null;
  if (!row) return kind ? NO(`No effects for ${kind} objects`) : GROUP_NO;
  return row[effectId] || NO('Unknown effect');
}

/** The recipe key a kind WRITES its slot bag under. Text stays on
 * data.textEffects (the zero-regression law); everything else writes the
 * generalized data.effects. */
export function effectsKeyFor(kind) {
  return kind === 'text' ? 'textEffects' : 'effects';
}

/** Dual-read of the slot bag from a DATA BAG (serialized JSON or live
 * obj.data): the generalized key first, the text-era key as the read-compat
 * alias. Designs saved before this lane keep loading forever. */
export function effectsBagOfData(data) {
  if (!data || typeof data !== 'object') return null;
  return data.effects ?? data.textEffects ?? null;
}

/** Dual-read of the slot bag from a live object. */
export function effectsBagOf(obj) {
  return effectsBagOfData(obj ? obj.data : null);
}
